import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/**
 * TechHistoryPage
 * ---------------------------------------------
 * Unified history view for Service / Contracts (Inspections) / WIP / Operations
 * Data source: tech_schedule_crud.php with keys:
 *   - ?getService=True
 *   - ?getContracts=True
 *   - ?getWip=True
 *   - ?getOp=True
 *
 * TailwindCSS for styling. Drop-in replacement for your existing schedule view,
 * but focused on HISTORY (completed/cancelled/past). Defaults to the last 30 days.
 */

const TYPE_META = {
  service:    { label: "Service",     badge: "bg-blue-100 text-blue-800 border-blue-300" },
  inspection: { label: "Inspection",  badge: "bg-yellow-100 text-yellow-800 border-yellow-300" },
  wip:        { label: "WIP",         badge: "bg-purple-100 text-purple-800 border-purple-300" },
  operations: { label: "Operations",  badge: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

function cx(...xs) { return xs.filter(Boolean).join(" "); }

// --- Date helpers (LOCAL, no UTC surprises) ---------------------------------
function parseLocalYMD(ymd) {
  if (typeof ymd === "string") {
    const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return null;
}
function toLocalISO(dateLike) {
  if (typeof dateLike === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateLike)) return dateLike;
  let d = null;
  if (typeof dateLike === "string") d = parseLocalYMD(dateLike) || new Date(dateLike);
  else d = new Date(dateLike);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function formatLocalYMDLabel(ymd) {
  const d = parseLocalYMD(ymd) || new Date();
  return d.toLocaleDateString(undefined, { weekday: "short", year: "numeric", month: "short", day: "numeric" });
}
function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toLocalISO(d);
}
function eachDayInclusive(fromISO, toISO) {
  if (!fromISO && !toISO) return [];
  const start = parseLocalYMD(fromISO || toISO);
  const end = parseLocalYMD(toISO || fromISO);
  if (!start || !end) return [];
  const out = [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    out.push(toLocalISO(d));
  }
  return out;
}

function formatTime(t) {
  if (!t) return "";
  const [hh, mm = "00"] = String(t).split(":");
  const h = parseInt(hh || "0", 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${mm.padStart(2, "0")} ${ampm}`;
}

function groupByDate(items) {
  const map = {};
  for (const it of items) (map[it._date] ||= []).push(it);
  return Object.entries(map)
    .sort(([a],[b]) => a.localeCompare(b))
    .map(([date, rows]) => ({ date, rows: rows.sort((x, y) => (x.start_time || "").localeCompare(y.start_time || "")) }));
}

export default function TechHistoryPage() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [raw, setRaw] = useState([]);
  const [search, setSearch] = useState("");

  // history defaults: last 30 days
  const [fromDate, setFromDate] = useState(daysAgo(30));
  const [toDate, setToDate] = useState(toLocalISO(new Date()));

  const [filters, setFilters] = useState({ service: true, inspection: true, wip: true, operations: true });
  const [onlyCompleted, setOnlyCompleted] = useState(true);
  const [includeCanceled, setIncludeCanceled] = useState(true);

  // const [selected, setSelected] = useState(null); - commented for full page - technical history view

  // state
  const [techFilter, setTechFilter] = useState("ALL");

  const toggleFilter = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));

  // -------- Normalizers per endpoint shape (based on your PHP outputs) -------
  // --- SERVICE ---
  const normalizeService = (arr) => (Array.isArray(arr) ? arr : []).map((row) => {
    const date = toLocalISO(row.SCHEDULE_DATE || row.DATE || new Date());
    const status = (row.STATUS || "").toLowerCase();

    const addTech = Array.isArray(row.additional_tech) ? row.additional_tech : [];
    const additionalTechNames = addTech.map(t => t?.tech_name?.trim()).filter(Boolean);

    return {
      _type: "service",
      id: row.id,
      _date: date,
      start_time: row.TIME || null,
      end_time: row.end_time || null,
      job_site: row.ADDRESS || "",
      customer: row.CUSTOMER || "",
      status,
      notes: row.NOTES || "",

      logged_by: row.USER || "",

      meta: {
        called_by: row.CALLED_BY || "",
        phone: row.PHONE || "",
        tech: row.TECH || "",
        tech_notes: row.TECHNOTES || "",
        time_log: row.TIME_LOG || "",
      },
      serv_tech_name: row.TECH || "",
      additional_tech: addTech,
      // NEW:
      lead_tech: (row.TECH || "").trim(),
      additionalTechNames,
      TROUBLE: row.TROUBLE || "",
      _canceled: status.includes("cancel"),
    };
  });

  // --- INSPECTION ---
  const normalizeInspection = (arr) => {
    const input = Array.isArray(arr) ? arr : [];
    const out = [];
    for (const log of input) {
      const fromISO = toLocalISO(log.schedule_date_from || log.scheduled_due_date || log.schedule_date_to || new Date());
      const toISO   = toLocalISO(log.schedule_date_to   || log.scheduled_due_date || log.schedule_date_from || new Date());
      const status = (log.inspection_status || log.status || "").toLowerCase();

      const addTech = Array.isArray(log.additional_tech) ? log.additional_tech : [];
      const additionalTechNames = addTech
        .map(t => (t?.name || t?.tech_name || "").trim())
        .filter(Boolean);

      for (const day of eachDayInclusive(fromISO, toISO)) {
        out.push({
          _type: "inspection",
          id: log.inspection_id,
          _date: day,
          start_time: log.schedule_time_in || null,
          end_time: log.schedule_time_out || null,
          job_site: [log.job_site_address, log.job_site_city, log.job_site_state, log.job_site_zip].filter(Boolean).join(", "),
          customer: log.customer_name || "",
          status,
          notes: log.inspection_notes || log.notes || "",
          meta: {
            contact_name: log.contact_name,
            contact_phone: log.contact_phone,
            contact_email: log.contact_email,
            contact_list: Array.isArray(log.contact_list) ? log.contact_list : [],
            schedule_from: fromISO,
            schedule_to: toISO,
            tech_name: log.tech_name || "",
            tech_notes: log.tech_notes || "",
          },
          tech_name: log.tech_name || "",
          additional_tech: addTech,
          // NEW:
          lead_tech: (log.tech_name || "").trim(),
          additionalTechNames,
          _canceled: status.includes("cancel"),
        });
      }
    }
    return out;
  };

  // --- WIP ---
  const normalizeWip = (arr) => {
    const input = Array.isArray(arr) ? arr : [];
    const out = [];
    for (const w of input) {
      const fromISO = toLocalISO(w.schedule_from || w.schedule_to || new Date());
      const toISO   = toLocalISO(w.schedule_to   || w.schedule_from || new Date());
      const status = (w.status || "").toLowerCase();

      // `w.additional_tech` appears to be a string in your data; normalize to array of names.
      const additionalTechNames = Array.isArray(w.additional_tech)
        ? w.additional_tech.map(t => (t?.name || t?.tech_name || "").trim()).filter(Boolean)
        : (typeof w.additional_tech === "string" && w.additional_tech.trim())
          ? [w.additional_tech.trim()]
          : [];

      for (const day of eachDayInclusive(fromISO, toISO)) {
        out.push({
          _type: "wip",
          id: w.schedule_id,
          _date: day,
          start_time: w.start_time || null,
          end_time: w.end_time || null,
          job_site: w.job_site || "",
          customer: w.customer_name || "",
          status,
          notes: w.job_description || "",
          meta: {
            job_number: w.job_number,
            type: w.type,
            coordinator_comment: w.user_comment,
            schedule_from: fromISO,
            schedule_to: toISO,
            tech_notes: w.tech_notes || "",
          },
          links: w.links || [],
          logged_by: w.user || "",
          tech_name: w.tech_name || "",
          additional_tech: w.additional_tech || "",
          // NEW:
          lead_tech: (w.tech_name || "").trim(),
          additionalTechNames,
          _canceled: status.includes("cancel"),
        });
      }
    }
    return out;
  };

  // --- OPERATIONS ---
  const normalizeOps = (arr) => (Array.isArray(arr) ? arr : []).map((o) => {
    const date = toLocalISO(o.schedule_from || o.date || new Date());
    const status = (o.status || "").toLowerCase();
    return {
      _type: "operations",
      id: o.id,
      _date: date,
      start_time: o.start_time || null,
      end_time: o.end_time || null,
      job_site: o.job_site || "",
      customer: o.customer_name || "",
      status,
      notes: o.description || "",
      
      logged_by: o.user || "",

      meta: {
        tech_name: o.tech_name || "",
        time_log: o.time_log || "",
        coordinator_comment: o.user_comment || "",
        raw_date: o.date || null,
      },
      // NEW:
      lead_tech: (o.tech_name || "").trim(),
      additionalTechNames: [], // if ops has additional techs later, normalize here
      _canceled: status.includes("cancel"),
    };
  });

  // build unique tech list from current data
  const techOptions = useMemo(() => {
    const set = new Set();
    for (const r of raw) {
      const lead = (r.lead_tech || r.tech_name || r.serv_tech_name || "").trim();
      if (lead) set.add(lead);
      if (Array.isArray(r.additionalTechNames)) {
        r.additionalTechNames.forEach(n => n && set.add(n));
      }
    }
    return ["ALL", ...Array.from(set).sort((a,b) => a.localeCompare(b))];
  }, [raw]);

    // --------------------------- Fetch ----------------------------------------
const fetchAll = useCallback(async (opts = {}) => {
  const from = opts.from ?? fromDate;
  const to   = opts.to   ?? toDate;
  const qRaw = opts.q ?? search;
  const q    = (qRaw || "").trim();

  const params = new URLSearchParams();
  if (from) params.append("from", from);
  if (to)   params.append("to", to);
  if (q)    params.append("q", q);   // only send if present

  setLoading(true);
  try {
    const [serv, insp, wip, ops] = await Promise.all([
      axios.get(`${apiUrl}/tech_schedule_crud.php?getService=True&${params.toString()}`),
      axios.get(`${apiUrl}/tech_schedule_crud.php?getContracts=True&${params.toString()}`),
      axios.get(`${apiUrl}/tech_schedule_crud.php?getWip=True&${params.toString()}`),
      axios.get(`${apiUrl}/tech_schedule_crud.php?getOp=True&${params.toString()}`),
    ]);

      // ... keep the rest of your normalize/merge code exactly as is ...
      const isServArrayLike = (d) => Array.isArray(d) || (d && typeof d === 'object' && ('0' in d) && ('1' in d));
      const serviceData = (isServArrayLike(serv.data) && serv.data[0] === 'success' && Array.isArray(serv.data[1])) ? serv.data[1] : [];
      const inspectionData = Array.isArray(insp.data?.success) ? insp.data.success : [];
      const wipData = Array.isArray(wip.data) && wip.data[0] === "success" && Array.isArray(wip.data[1]) ? wip.data[1] : [];

      let opsData = [];
      if (Array.isArray(ops.data) && ops.data[0] === 'success') {
        opsData = Array.isArray(ops.data[1]) ? ops.data[1] : [];
      } else if (ops.data?.success && Array.isArray(ops.data.data)) {
        opsData = ops.data.data;
      }

      const merged = [
        ...normalizeService(serviceData),
        ...normalizeInspection(inspectionData),
        ...normalizeWip(wipData),
        ...normalizeOps(opsData),
      ];
      setRaw(merged);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/login");
      else if (err?.response?.status === 403) toast.error("Access denied.", { position: "top-center" });
      else {
        toast.error("Error loading history.", { position: "top-center" });
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [apiUrl, navigate, search, fromDate, toDate]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // --- Controls UI ---
  const onSubmitSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) {
      toast.info("Type a job site to search its full history.", { position: "top-center" });
      return;
    }
    fetchAll({ q: search });
  };

    // ------------------------ Filter + Search + Range --------------------------
  const filtered = useMemo(() => {
    let a = fromDate, b = toDate;
    if (a && b && a > b) [a, b] = [b, a];
    const inRange = (d) => (!a || d >= a) && (!b || d <= b);
    const q = (search || "").toLowerCase();

    const techMatches = (row) => {
      if (techFilter === "ALL") return true;
      const lead = (row.lead_tech || row.tech_name || row.serv_tech_name || "").trim().toLowerCase();
      const adds = Array.isArray(row.additionalTechNames)
        ? row.additionalTechNames.map(n => n.toLowerCase())
        : [];
      return lead === techFilter.toLowerCase() || adds.includes(techFilter.toLowerCase());
    };

    return raw
      .filter((it) => filters[it._type])
      .filter((it) => inRange(it._date))
      .filter((it) => {
        const status = (it.status || "").toLowerCase();
        const isCompleted = status.includes("complete");
        const isCanceled = status.includes("cancel");
        if (onlyCompleted && !isCompleted) return false;
        if (!includeCanceled && isCanceled) return false;
        return true;
      })
      .filter(techMatches)                           // <-- TECH FILTER APPLIED HERE
      .filter((it) =>
        !q ||
        (it.job_site || "").toLowerCase().includes(q) ||
        (it.customer || "").toLowerCase().includes(q) ||
        (it.notes || "").toLowerCase().includes(q)
      );
    }, [raw, filters, fromDate, toDate, search, onlyCompleted, includeCanceled, techFilter]);
    const grouped = useMemo(() => groupByDate(filtered), [filtered]);

  
  const openHistoryDetails = (item) => {
  const payload = { item };

  const storageKey =
    `techHistoryDetails:${item._type}:${item.id}:${item._date}`;

  sessionStorage.setItem(
    storageKey,
    JSON.stringify(payload)
  );

  navigate(
    `/t/history-details/${item._type}/${item.id}?date=${encodeURIComponent(item._date)}`,
    {
      state: payload,
    }
  );
  };


  // ------------------------------ UI ----------------------------------------
  return (
    <div className="mx-auto max-w-[1100px] p-4">
      <h1 className="text-xl font-bold mb-3">Schedule History</h1>

      {/* Controls: search + range + toggles */}
      <div className="grid grid-cols-1 md:grid-cols-7 gap-3 mb-4">
        {/* Search + button, full width on mobile, 2 cols on md */}
        <form onSubmit={onSubmitSearch} className="flex md:col-span-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search job site…"
            className="flex-1 border rounded-l px-3 py-2"
          />
          <button
            type="submit"
            className="rounded-r bg-blue-600 text-white px-3 py-2 hover:bg-blue-700"
            disabled={loading}
            title="Search by job site (server-side)"
          >
            {loading ? "…" : "Search"}
          </button>
        </form>

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="border rounded px-3 py-2"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="border rounded px-3 py-2"
        />
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={() => {
              const from = daysAgo(7);
              const to = toLocalISO(new Date());
              setFromDate(from);
              setToDate(to);
              fetchAll({ from, to, q: search });
            }}
            className="px-2 py-1 border rounded"
          >
            Last 7d
          </button>

          <button
            onClick={() => {
              const from = daysAgo(30);
              const to = toLocalISO(new Date());
              setFromDate(from);
              setToDate(to);
              fetchAll({ from, to, q: search });
            }}
            className="px-2 py-1 border rounded"
          >
            30d
          </button>

          <button
            onClick={() => {
              const from = daysAgo(90);
              const to = toLocalISO(new Date());
              setFromDate(from);
              setToDate(to);
              fetchAll({ from, to, q: search });
            }}
            className="px-2 py-1 border rounded"
          >
            90d
          </button>
        </div>

        {/* <select
          value={techFilter}
          onChange={(e) => setTechFilter(e.target.value)}
          className="border rounded px-3 py-2"
          title="Filter by technician"
        >
          {techOptions.map(opt => (
            <option key={opt} value={opt}>
              {opt === "ALL" ? "All Techs" : opt}
            </option>
          ))}
        </select> */}

      </div>

      {/* Type filters + refresh */}
      <div className="flex flex-wrap gap-2 mb-4">
        {Object.entries(TYPE_META).map(([key, meta]) => (
          <button
            key={key}
            onClick={() => toggleFilter(key)}
            className={cx(
              "px-3 py-1 rounded-full border text-sm",
              filters[key] ? meta.badge : "bg-white text-gray-500 border-gray-300"
            )}
          >
            {meta.label}
          </button>
        ))}
        <button
          onClick={() => fetchAll({ q: search })}
          className="ml-auto bg-blue-600 text-white px-3 py-1.5 rounded shadow hover:bg-blue-700"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>

      </div>

      {/* Empty state */}
      {grouped.length === 0 && !loading && (
        <div className="text-gray-600">No history matches your filters.</div>
      )}

      {/* Date-grouped list */}
      <div className="space-y-6">
        {grouped.map(({ date, rows }) => (
          <section key={date}>
            <div className="text-sm text-gray-500 mb-2">{formatLocalYMDLabel(date)}</div>
            <ul className="space-y-2">
              {rows.map((ev, i) => {
                const meta = TYPE_META[ev._type] || { badge: "bg-gray-100 text-gray-700 border-gray-300" };
                const canceled = ev._canceled;
                const strike = canceled ? "line-through text-gray-400" : "";
                return (
                  <li key={`${date}-${ev._type}-${ev.id || i}`} className="bg-green-50 border border-green-300 rounded-md shadow-sm p-3">
                    <button onClick={() => openHistoryDetails(ev)} className="w-full text-left">
                      <div className="flex items-start gap-3">
                        {/* Time + badge */}
                        <div className="text-center min-w-[100px]">
                          <div className={cx("text-xs font-semibold", strike)}>
                            {formatTime(ev.start_time)}{ev.end_time ? `–${formatTime(ev.end_time)}` : ""}
                          </div>
                          <span className={cx("inline-block mt-1 px-2 py-0.5 text-xs border rounded", meta.badge)}>
                            {TYPE_META[ev._type]?.label || ev._type}
                          </span>
                        </div>
                        {/* Info */}
                        <div className="flex-1">
                          <div className={cx("font-semibold", strike)}>{ev.job_site || "(No Address)"}</div>
                          <div className={cx("text-sm text-gray-600", strike)}>{ev.customer || ""}</div>
                          {ev._type === "operations" && ev.notes && (
                            <div className={cx("text-sm mt-1", strike)}>
                              <span className="font-medium">Description: </span>{ev.notes}
                            </div>
                          )}
                          {!!ev.status && !canceled && (
                            <div className="text-xs text-gray-500 mt-1">Status: 
                              <span className=" bg-green-200 p-1 rounded-md font-bold text-black">{ev.status}</span>
                            </div>
                          )}
                          {canceled && (
                            <div className="text-xs text-red-600 mt-1 font-medium">Canceled</div>
                          )}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}
